import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import {
  Grid,
  Divider,
  Typography,
  useMediaQuery,
  useTheme,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  Button,
  Checkbox,
  RadioGroup,
  IconButton,
} from '@mui/material'

import { AttachFileOutlined } from '@mui/icons-material'
import { showSnackbar } from '@context/SnackbarContext'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import moment from 'moment'
import { GlobalStyle as gs } from '@src/GlobalStyle'
import { useSelector, useDispatch } from 'react-redux'
import AddrPopup from '@components/AddrPopup'
import RecvPopup from '@components/RecvPopup'
import WebService from '@util/webService'
import { comma, getAddress } from '@src/util/helper'
import useAlert from '@hooks/useAlert'
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined'
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined'
import { dataUrlToFile, getImageTypeFromDataURL } from '@util/helper'
import { checkFileSize, validateInputs, customSkipKeys } from '@pages/common/common'
import axios from 'axios'
import useIndicator from '@hooks/useIndicator'
import MinwonView from '@components/MinwonView'
import { max, rearg } from 'lodash'
import CloseIcon from '@mui/icons-material/Close';

// 상품/금액: AdminOrderView 밖에 정의하고 자체 state 로 관리
// (부모 state 를 바꾸면 TxtTmpl 이 새로 마운트되어 저장 전 입력값이 초기화되므로 order 객체에만 직접 반영)
const ProductAmountEditor = ({ order, productCodes }) => {
  const [productCode, set_productCode] = useState(order.productCode ?? '')
  const [amountDigits, set_amountDigits] = useState(order.amount != null ? String(order.amount) : '')

  // 자유결제(P100)는 정해진 상품금액이 없으므로 비교하지 않음
  // 우편물 실물 발송 서비스는 종료되어 상품가만 비교
  const product = productCodes.find((r) => r.code === productCode)
  const basePrice = +(product?.note1 ?? NaN)
  const expectedAmount =
    productCode !== 'P100' && Number.isFinite(basePrice)
      ? basePrice
      : null
  const isAmountDiff = expectedAmount != null && (+amountDigits || 0) !== expectedAmount

  return (
    <>
      <Grid item xs={12}>
        <Grid container spacing={2} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Grid item xs={2}>
            <Typography>상품</Typography>
          </Grid>
          <Grid item xs>
            <FormControl fullWidth>
              <Select
                value={productCode}
                onChange={(e) => {
                  set_productCode(e.target.value)
                  order.productCode = e.target.value
                }}
              >
                {productCodes.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Grid item xs={2}>
            <Typography>상품상세</Typography>
          </Grid>
          <Grid item xs>
            {/* validateInputs 대상이 아니므로 입력 즉시 order 에 반영 (product_detail varchar(500)) */}
            <TextField
              fullWidth
              multiline
              minRows={2}
              inputProps={{ maxLength: 500 }}
              defaultValue={order.productDetail ?? ''}
              onChange={(e) => {
                order.productDetail = e.target.value
              }}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Grid container spacing={2} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
          <Grid item xs={2}>
            <Typography>금액</Typography>
          </Grid>
          <Grid item xs>
            <TextField
              fullWidth
              value={amountDigits ? comma(+amountDigits) : ''}
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
              InputProps={{ endAdornment: <Typography>원</Typography> }}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '')
                set_amountDigits(digits)
                order.amount = +digits || 0
              }}
            />
            {isAmountDiff ? (
              <Typography variant='body2' color='error' style={{ marginTop: 4 }}>
                상품금액 다름
              </Typography>
            ) : null}
          </Grid>
        </Grid>
      </Grid>
    </>
  )
}

const AdminOrderView = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const codes = useSelector((state) => state.codeReducer)
  const productCodes = codes.filter((r) => r.codeGroupId === 'R001')
  const bizCodes = codes.filter((r) => r.codeGroupId === 'R002')
  const channelCodes = codes.filter((r) => r.codeGroupId === 'R003')
  const navigate = useNavigate()
  const location = useLocation()
  const orderId = location.state?.order?.id
  // const [order, set_order] = useState(location.state?.order)
  const [order, set_order] = useState({})
  const inputsRef = useRef({})
  const curLocationRef = useRef(location)
  const { alert, renderAlert } = useAlert()
  const dgRef = useRef()
  const [idCard, set_idCard] = useState('')
  const [signature, set_signature] = useState('')
  const [recvAmount, set_recvAmount] = useState(0)
  const [serviceAmount, set_serviceAmount] = useState(0)
  const [attchs, set_attchs] = useState([])
  const [openIndicator, closeIndicator, renderIndicator] = useIndicator()

  const removeNonDigits = (input) => {
    return input.replace(/\D/g, '')
  }

  const handleChange = (e) => {
    console.log('e=>', e.target.name)
    if (e.target.name === 'repPhone' || e.target.name === 'contactPhone') {
      const newVal = removeNonDigits(e.target.value)
      inputsRef.current[e.target.name].value = newVal
    }

    if (e.target.name === 'civilNum1' || e.target.name === 'civilNum2') {
      const newVal = removeNonDigits(e.target.value)
      inputsRef.current[e.target.name].value = newVal
      console.log('newVal =>', newVal)
    }

    if (e.target.name === 'bizNum1' || e.target.name === 'bizNum2' || e.target.name === 'bizNum3') {
      const newVal = removeNonDigits(e.target.value)
      inputsRef.current[e.target.name].value = newVal
    }

    if (e.target.name === 'corpNum1' || e.target.name === 'corpNum2') {
      const newVal = removeNonDigits(e.target.value)
      inputsRef.current[e.target.name].value = newVal
    }

    if (e.target.name === 'startDay' || e.target.name === 'endDay') {
      const newVal = removeNonDigits(e.target.value)
      inputsRef.current[e.target.name].value = newVal
    }
  }

  const fetchImages = async () => {
    if (!orderId) {
      return
    }
    const url = `/order/id/img/${orderId}`

    const resp = await WebService.get(url)
    if (resp.repCode !== 'ack') {
      alert(resp.repMessage)
      return
    }

    const repMessage = JSON.parse(resp.repMessage)
    const order = JSON.parse(repMessage.order)
    const attchs = JSON.parse(repMessage.attchs)

    order.startDay = moment(order.startTime, 'YYYY-MM-DD HH:mm:ss').format('YYYYMMDD')
    order.endDay = moment(order.endTime, 'YYYY-MM-DD HH:mm:ss').format('YYYYMMDD')
    set_order(order)
    set_attchs(attchs)
  }

  const filterImages = (catCode) => {
    return attchs.filter((r) => r.catCode === catCode)
  }

  const onLoad = async () => {
    fetchImages()
  }

  const onChangeFiles = (files, catCode) => {
    if(files.length == 0) {
      const arr = attchs.filter((r) => r.catCode !== catCode)
      set_attchs(arr)
      return
    }

    const ret = checkFileSize(files, theme)
    if (!ret) {
      return
    }

    const rowsPromises = files.map((f) => {
      return new Promise((resolve) => {
        const reader = new FileReader()
        reader.onload = () => {
          const row = {
            id: -1,
            tblName: 'orders',
            recordId: order.id,
            catCode: catCode,
            fileLoc: '',
            fileType: '', // Add the logic to determine file type if needed
            name: f.name,
            dataUrl: reader.result, // Assign the reader result to dataUrl
          }
          resolve(row) // Resolve the promise with the row
        }
        reader.readAsDataURL(f)
      })
    })

    // Wait for all files to be processed
    Promise.all(rowsPromises).then((rows) => {
      const arr = attchs.filter((r) => r.catCode !== catCode)
      const newAttchs = arr.concat(rows)
      set_attchs(newAttchs)
    })
  }

  const onDownload = (attach) => {
    const dataUrl = attach.dataUrl
    const suffix = getImageTypeFromDataURL(dataUrl)
    const companyName = order.companyName
    const catName =
      attach.catCode === 'A001'
        ? '신분증'
        : attach.catCode === 'A002'
          ? '사업자등록증'
          : attach.catCode === 'A003'
            ? '법인등기부등본'
            : attach.catCode === 'A004'
              ? '대리인위임장'
              : attach.catCode === 'A005'
                ? '서명'
                : ''
    const filename = `${companyName}_${catName}.${suffix}`
    const anchor = document.createElement('a')
    anchor.href = dataUrl
    anchor.download = filename
    anchor.click()
    URL.revokeObjectURL(dataUrl)
  }

  const onBack = () => {
    const pack = location.state?.pack
    if (pack?.url) {
      navigate(pack.url, {
        replace: true,
        state: {
          pack: pack,
        },
      })
    } else {
      navigate(-1)
    }
  }

  const extractFiles = () => {
    const idFiles = []
    const bizFiles = []
    const corpFiles = []
    const agencyFiles = []
    const sigFiles = []

    attchs.forEach((r) => {
      const file = dataUrlToFile(r.dataUrl, `${r.id}_${r.name}`)
      if (r.catCode === 'A001') {
        idFiles.push(file)
      } else if (r.catCode === 'A002') {
        bizFiles.push(file)
      } else if (r.catCode === 'A003') {
        corpFiles.push(file)
      } else if (r.catCode === 'A004') {
        agencyFiles.push(file)
      } else if (r.catCode === 'A005') {
        sigFiles.push(file)
      }
    })
    return { idFiles: idFiles, bizFiles: bizFiles, corpFiles: corpFiles, agencyFiles: agencyFiles, sigFiles: sigFiles }
  }

  const onSubmit = async () => {
    // 상품/금액은 입력 즉시 order 에 반영: 입력값만 검사하고 관리자가 입력한 금액을 그대로 저장
    // (validate 는 amount 를 다시 계산하므로 사용하지 않음)
    // 자유결제(P100)는 업종/주소/사업자 유형 없이 생성되므로 해당 항목 검사 제외
    const skipKeys = order.productCode === 'P100' ? customSkipKeys : []
    const ret = validateInputs(order, inputsRef, theme, skipKeys)
    if (!ret) {
      return
    }

    const startDay = inputsRef.current['startDay'].value
    const endDay = inputsRef.current['endDay'].value

    const dateFormat = 'YYYY-MM-DD HH:mm:ss'
    if (!moment(startDay, 'YYYYMMDD', true).isValid()) {
      showSnackbar('시작일이 올바르지 않습니다.', theme.palette.warning.dark)
      inputsRef.current['startDay'].focus()
      return
    }

    order.startDay = startDay
    order.startTime = moment(startDay, 'YYYYMMDD').format(dateFormat)

    if (!moment(endDay, 'YYYYMMDD', true).isValid()) {
      showSnackbar('종료일이 올바르지 않습니다.', theme.palette.warning.dark)
      inputsRef.current['endDay'].focus()
      return
    }
    order.endDay = inputsRef.current['endDay'].value
    order.endTime = moment(endDay, 'YYYYMMDD').format(dateFormat)

    const { idFiles, bizFiles, corpFiles, agencyFiles, sigFiles } = extractFiles()

    const params = {}
    const formData = new FormData()

    if (idFiles.length > 0) {
      formData.append('idFiles', idFiles[0])
    }

    if (bizFiles && bizFiles.length > 0) {
      formData.append('bizFiles', bizFiles[0])
    }

    if (corpFiles && corpFiles.length > 0) {
      corpFiles.forEach((f) => {
        formData.append('corpFiles', f)
      })
    }
    if (agencyFiles && agencyFiles.length > 0) {
      formData.append('agencyFiles', agencyFiles[0])
    }
    if (sigFiles && sigFiles.length > 0) {
      formData.append('sigFiles', sigFiles[0])
    }

    const data = {
      order: order,
    }

    const jsonData = JSON.stringify(data)
    formData.append('body', new Blob([jsonData], { type: 'application/json' }))

    const url = `/order/update`
    openIndicator()
    const resp = await axios.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      params: params,
    })
    closeIndicator()

    if (resp.data.repCode !== 'ack') {
      alert(resp.data.repMessage)
      return
    }
    // alert('처리 되었습니다.')

    set_order({ ...order })
    showSnackbar('처리 되었습니다.')
  }

  useEffect(() => {
    if (order.id) {
      for (const [key, value] of Object.entries(order)) {
        const target = {
          name: key,
          value: value,
        }
        if (inputsRef.current[key]) {
          inputsRef.current[key].name = target.name
          inputsRef.current[key].value = target.value
        } else {
          inputsRef.current[key] = target
        }
      }
    }
  }, [order])

  useEffect(() => {
    if (curLocationRef.current.key !== location.key) {
      curLocationRef.current = location
      onLoad()
    }
  }, [location])

  useEffect(() => {
    onLoad()
  }, [])

  const TxtTmpl = ({ label, field, required, maxLength, readOnly = false, shrink = false }) => {
    const maxLength2 = maxLength ?? 32
    return (
      <TextField
        label={label}
        name={field}
        required={required}
        inputProps={{
          maxLength: maxLength2,
          readOnly: readOnly,
        }}
        // 코드로 값을 넣는 칸(주소 찾기)은 라벨이 겹치지 않도록 항상 위에 표시
        InputLabelProps={shrink ? { shrink: true } : undefined}
        fullWidth
        defaultValue={order[field]}
        inputRef={(el) => (inputsRef.current[field] = el)}
        onChange={handleChange}
      />
    )
  }

  const RadioTmpl = ({ pairs, field, disabled = false, onChanged }) => {
    return (
      <RadioGroup
        row
        defaultValue={order[field]}
        onChange={(e) => {
          order[field] = e.target.value
          if (field === 'bizCode') {
            set_order({ ...order })
          }
          onChanged?.(e.target.value)
        }}
      >
        {pairs.map((code, idx) => (
          <FormControlLabel key={idx} value={code.code} label={code.name} control={<Radio />} disabled={disabled} />
        ))}
      </RadioGroup>
    )
  }

  const GridImgTmpl = ({ label, catCode, field, imgWidth, changeable, multiple = false }) => {
    return (
      <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
        <Grid item xs={2}>
          <Typography>{label}</Typography>
        </Grid>

        <Grid item xs={8}>
          {filterImages(catCode).map((r, idx) => (
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <img key={idx} src={r.dataUrl} alt={label} style={{ width: +imgWidth }} />

              <div>
                <IconButton
                  onClick={() => {
                    onDownload(r)
                  }}
                >
                  <FileDownloadOutlined />
                </IconButton>
              </div>
              {
                r.catCode !== 'A001' && r.catCode !== 'A005' ? (
                   <div>
                      <IconButton
                        onClick={() => {
                          onChangeFiles([], catCode)
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </div>
                ) : null
              }
            </div>
          ))}
        </Grid>
        {changeable ? (
          <Grid item xs={2}>
            <input
              type='file'
              style={{ display: 'none' }}
              accept='.jpg, .jpeg, .png'
              multiple={multiple}
              ref={(el) => (inputsRef.current[field] = el)}
              onChange={(e) => {
                const files = [...e.target.files]
                onChangeFiles(files, catCode)
              }}
            />
            <IconButton
              component='label'
              onClick={() => {
                inputsRef.current[field]?.click()
              }}
            >
              <AttachFileOutlined />
            </IconButton>
          </Grid>
        ) : null}
      </Grid>
    )
  }

  return (
    <div style={{ paddingTop: isMobile ? 10 : 10 }}>
      {renderAlert()}
      {renderIndicator()}

      <AddrPopup
        ref={dgRef}
        title={'주소검색'}
        windowWidth='sm'
        callback={(data) => {
          if (data.zonecode) {
            inputsRef.current['zipCode'].value = data.zonecode
            const addr = getAddress(data, true, true)
            inputsRef.current['addr1'].value = addr
          } else {
            inputsRef.current['zipCode'].value = ''
            inputsRef.current['addr1'].value = ''
          }
        }}
      />
      <Container maxWidth='md'>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <IconButton
              style={{ backgroundColor: '#fafafa', marginRight: 10 }}
              onClick={() => {
                onBack()
              }}
            >
              <ArrowBackOutlined />
            </IconButton>
          </Grid>

          <Grid item xs={12}>
            <Typography variant='h5' style={{ fontWeight: 'bold' }}>
              계약 내용
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography style={{ fontWeight: 'bold' }} variant='h6'>
                  {order.branch?.branchName} {order.room?.roomNo} 호
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TxtTmpl label='대표자명' field='repName' required={true} />
              </Grid>
              <Grid item xs={6}>
                <TxtTmpl label='대표자 연락처' field='repPhone' required={true} maxLength={11} />
                {/* <TxtTmpl label='대표자 연락처' field='repPhone' required={true}/> */}
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TxtTmpl label='담당자명' field='contactName' required={true} />
              </Grid>
              <Grid item xs={6}>
                <TxtTmpl label='담당자 연락처' field='contactPhone' required={true} maxLength={11} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TxtTmpl label='상호명' field='companyName' required={true} />
              </Grid>
              <Grid item xs={6}>
                <TxtTmpl label='업종' field='industry' required={true} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TxtTmpl label='이메일' field='email' required={true} />
              </Grid>
              <Grid item xs={6}>
                <Grid
                  container
                  spacing={0}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Grid item xs={5}>
                    <TxtTmpl label='주민번호 앞' field='civilNum1' required={true} maxLength={6} />
                  </Grid>
                  <Grid item xs={2}>
                    <Typography textAlign={'center'}>-</Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <TxtTmpl label='주민번호 뒤' field='civilNum2' required={true} maxLength={7} />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={2}>
                <TxtTmpl label='우편번호' field='zipCode' required={true} maxLength={100} readOnly={true} shrink />
              </Grid>
              <Grid item xs={6}>
                <Button
                  size='medium'
                  onClick={() => {
                    dgRef.current.open()
                  }}
                >
                  주소 찾기
                </Button>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={8}>
                <TxtTmpl label='주소' field='addr1' required={true} maxLength={100} readOnly={true} shrink />
              </Grid>
              <Grid item xs={4}>
                <TxtTmpl label='상세 주소' field='addr2' required={true} maxLength={100} />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TxtTmpl label='시작일' field='startDay' required={true} maxLength={8} />
              </Grid>
              <Grid item xs={6}>
                <TxtTmpl label='종료일' field='endDay' required={true} maxLength={8} />
              </Grid>
            </Grid>
          </Grid>
          {/* 주문 조회 후 조회값으로 다시 초기화되도록 order.id 로 key 지정 */}
          <ProductAmountEditor
            key={`pa_${order.id ?? ''}`}
            order={order}
            productCodes={productCodes}
          />
          <Grid item xs={12}>
            <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={2}>
                <Typography>사업자 유형*</Typography>
              </Grid>
              <Grid item xs>
                <RadioTmpl pairs={bizCodes} field='bizCode' />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GridImgTmpl label='대표자 신분증*' catCode='A001' field='idFiles' imgWidth='50' changeable={true} />
          </Grid>

          <Grid item xs={12}>
            <GridImgTmpl label='사업자 등록증' catCode='A002' field='bizFiles' imgWidth='50' changeable={true} />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={2}>
                <Typography>사업자등록번호</Typography>
              </Grid>
              <Grid item xs>
                <TxtTmpl label='' field='bizNum1' required={false} maxLength={3} />
              </Grid>
              <Grid item xs>
                <TxtTmpl label='' field='bizNum2' required={false} maxLength={2} />
              </Grid>
              <Grid item xs>
                <TxtTmpl label='' field='bizNum3' required={false} maxLength={5} />
              </Grid>
            </Grid>
          </Grid>
          {order.bizCode == 'B002' ? (
            <>
              <Grid item xs={12}>
                <GridImgTmpl
                  label='법인 등본'
                  catCode='A003'
                  field='corpFiles'
                  imgWidth='50'
                  changeable={true}
                  multiple
                />
              </Grid>

              <Grid item xs={12}>
                <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
                  <Grid item xs={2}>
                    <Typography>법인등록번호</Typography>
                  </Grid>
                  <Grid item xs>
                    <TxtTmpl label='' field='corpNum1' required={false} maxLength={6} />
                  </Grid>
                  <Grid item xs>
                    <TxtTmpl label='' field='corpNum2' required={false} maxLength={7} />
                  </Grid>
                </Grid>
              </Grid>
            </>
          ) : null}

          <Grid item xs={12}>
            <GridImgTmpl label='대리인 위임장' catCode='A004' field='agencyFiles' imgWidth='50' changeable={true} />
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={4}>
                <Typography>우편물 대리 개봉 및 메일 발송 서비스*</Typography>
              </Grid>
              <Grid item xs>
                <RadioTmpl
                  pairs={[
                    { code: 'Y', name: '신청' },
                    { code: 'N', name: '미신청' },
                  ]}
                  field='emailSvcYn'
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={4}>
                <Typography>우편물, 택배 보관 기간 초과 시 폐기 동의*</Typography>
              </Grid>
              <Grid item xs>
                <RadioTmpl
                  pairs={[
                    { code: 'Y', name: '동의' },
                    { code: 'N', name: '미동의' },
                  ]}
                  field='destroySvcYn'
                />
              </Grid>
            </Grid>
          </Grid>

          {/* 기장 여부/비고: validateInputs 대상이 아니므로 입력 즉시 order 에 반영 */}
          <Grid item xs={12}>
            <Grid container spacing={2} style={{ display: 'flex', alignItems: 'center' }}>
              <Grid item xs={4}>
                <Typography>기장 여부</Typography>
              </Grid>
              <Grid item xs>
                <RadioTmpl
                  pairs={[
                    { code: 'Y', name: 'Y' },
                    { code: 'N', name: 'N' },
                  ]}
                  field='bookYn'
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            {/* 주문 조회 후 defaultValue 를 다시 반영하도록 order.id 로 key 지정 */}
            <TextField
              key={`bookRemark_${order.id ?? ''}`}
              label='기장 비고'
              fullWidth
              multiline
              rows={3}
              inputProps={{ maxLength: 500 }}
              defaultValue={order.bookRemark ?? ''}
              onChange={(e) => {
                order.bookRemark = e.target.value
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <GridImgTmpl label='서명*' catCode='A005' field='sigFiles' imgWidth='50' changeable={true} />
          </Grid>

          <Grid
            item
            xs={12}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button style={{ marginRight: 10 }} onClick={onBack}>
              뒤로가기
            </Button>
            <Button variant='contained' onClick={onSubmit}>
              저장
            </Button>
          </Grid>

          <Grid item xs={12}>
            {' '}
          </Grid>
        </Grid>
      </Container>
    </div>
  )
}

export default AdminOrderView
